import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  runInInjectionContext,
  viewChild,
} from '@angular/core';
import { CaseBattlesCreateService } from '../services/case-battles-create.service';
import { CaseBattlesService } from '../case-battles.service';
import { CaseBattleAnimationEnum } from '../interfaces/case-battles.interface';
import { ICaseBattle } from '../interfaces/case-battles.interface';
import { CurrencyPipe } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { NgpTooltipTrigger, NgpTooltip } from 'ng-primitives/tooltip';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { CbListModeComponent } from '../cb-list-mode/cb-list-mode.component';
import { ClipboardService } from '@core/services/clipboard.service';
import { CbCasesProgressComponent } from '../cb-cases-progress/cb-cases-progress.component';
import {
  fadeInAnimation,
  fadeInOutAnimation,
} from '@core/utils/animations.util';
import NumberFlow from 'number-flow';
import { CostComponent } from '../../../core/components/cost/cost.component';
import { BalanceType } from '@core/types/balance.type';
import { UIService } from '@core/services/ui.service';

@Component({
  selector: 'app-cb-battle-header',
  templateUrl: './cb-battle-header.component.html',
  styleUrls: ['./cb-battle-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CurrencyPipe,
    SvgIconComponent,
    NgpTooltip,
    NgpTooltipTrigger,
    BalanceIconComponent,
    OverlayModule,
    CbListModeComponent,
    CbCasesProgressComponent,
    CostComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [fadeInOutAnimation, fadeInAnimation],
})
export class CbBattleHeaderComponent {
  public battlesService = inject(CaseBattlesService);
  public cbCreateService = inject(CaseBattlesCreateService);
  public clipboardService = inject(ClipboardService);
  private injector = inject(Injector);
  public uiService = inject(UIService);

  battle = input.required<ICaseBattle>();
  animation = input.required<keyof typeof CaseBattleAnimationEnum | null>();
  replayed = input<boolean>(false);
  balance = input.required<BalanceType>();

  rehostBattle = output();
  replayBattle = output();

  currentRound = viewChild<ElementRef<NumberFlow>>('currentRound');
  roundCost = viewChild<ElementRef<NumberFlow>>('roundCost');

  readonly AFFILIATE_TIME: Record<string, string> = {
    '1d': 'day',
    '2d': '2 days',
    '1w': 'week',
    '1m': 'month',
  };

  isOpenReactionsPopover = false;

  casesProgressSettings = computed<{ perView: number }>(() => {
    const breakpoints = this.uiService.breakpoints();
    const settings: Record<
      Exclude<keyof typeof breakpoints, 'max_lg' | 'max_xl2'>,
      { perView: number }
    > = {
      xs: { perView: 2 },
      xsm: { perView: 3 },
      xsl: { perView: 4 },
      sm: { perView: 5 },
      md: { perView: 9 },
      lg: { perView: 11 },
      xl: { perView: 6 },
      xl2: { perView: 6 },
      xl3: { perView: 6 },
      xl4: { perView: 9 },
    };

    const activeBreakpoint = Object.keys(breakpoints).find(
      (key) => breakpoints[key as keyof typeof breakpoints]
    );
    const activeBreakpointSettings =
      settings[activeBreakpoint as keyof typeof settings];

    return activeBreakpointSettings;
  });

  constructor() {
    afterRenderEffect(() => {
      if (this.currentRound()?.nativeElement && this.battle().rounds.length) {
        this.currentRound()!.nativeElement.update(this.battle().rounds.length);
      }
      if (this.roundCost()?.nativeElement && this.battle().rounds.length) {
        this.roundCost()!.nativeElement.update(this.getRoundCost());
      }
    });
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.injector, (): void => {
      if (this.currentRound()?.nativeElement) {
        this.currentRound()!.nativeElement.transformTiming = {
          duration: 300,
          //easing: 'linear',
        };
        this.currentRound()!.nativeElement.spinTiming = {
          duration: 300,
          //easing: 'linear',
        };
        this.currentRound()!.nativeElement.opacityTiming = {
          duration: 300,
          //easing: 'linear',
        };

        this.currentRound()!.nativeElement.update(this.battle().rounds.length);
      }
      if (this.roundCost()?.nativeElement) {
        this.roundCost()!.nativeElement.format = {
          minimumIntegerDigits: 1,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        };
        this.roundCost()!.nativeElement.locales = 'en-US';
        this.roundCost()!.nativeElement.transformTiming = {
          duration: 300,
          //easing: 'linear',
        };
        this.roundCost()!.nativeElement.spinTiming = {
          duration: 300,
          //easing: 'linear',
        };
        this.roundCost()!.nativeElement.opacityTiming = {
          duration: 300,
          //easing: 'linear',
        };
        this.roundCost()!.nativeElement.update(
          this.battle().status === 'open' ? 0.0 : this.getRoundCost()
        );
      }
    });
  }

  private getRoundCost() {
    const { cases, rounds } = this.battle();
    const casePrice = cases[rounds.length - 1].price;
    return casePrice;
  }

  public resetCosts() {
    if (this.currentRound()?.nativeElement) {
      this.currentRound()!.nativeElement.update(0);
    }
    if (this.roundCost()?.nativeElement) {
      this.roundCost()!.nativeElement.update(0.0);
    }
  }
}
